import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { FormModalBaseComponent } from '../../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { AddressService } from '../../../../../../core/services/address.service';
import { AddressModel } from '../../../../../../core/models/address.model';
import { Country } from '../../../../../../core/enums/countries.enum';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { CountryGroupsHelper } from '../../../../../../core/helpers/country-groups.helper';

@Component({
  selector: 'vex-building-address-modal',
  templateUrl: './building-address-modal.component.html',
  styleUrls: ['./building-address-modal.component.scss'],
})
export class BuildingAddressModalComponent extends FormModalBaseComponent<AddressModel> implements OnInit {
  // Permissions
  ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  canCreateMode = true;
  canUpdateMode = true;
  canDeleteMode = true;

  // Data for form controls - Pays groupés par continent
  countryGroups = CountryGroupsHelper.getGroupedCountries().map(group => ({
    label: group.label,
    items: group.countries,
  }));

  // Building relation
  buildingId: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FormModalBaseComponent<AddressModel>>,
    public addressService: AddressService,
  ) {
    super(data, dialogRef, addressService);
    this.buildingId = data?.buildingId || (data?.buildingIds && data?.buildingIds[0]);
  }

  createForm(): void {
    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(this.defaults?._id),
      label: new UntypedFormControl(this.defaults?.label),
      street: new UntypedFormControl(this.defaults?.street, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(255),
      ]),
      additionalAddress: new UntypedFormControl(this.defaults?.additionalAddress, [Validators.maxLength(255)]),
      postalCode: new UntypedFormControl(this.defaults?.postalCode, [Validators.required]),
      city: new UntypedFormControl(this.defaults?.city, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
      ]),
      country: new UntypedFormControl(this.defaults?.country || Country.FR, [Validators.required]),
      buildingIds: new UntypedFormControl([this.buildingId] || []),
    });

    // Mise à jour des validateurs lors du changement de pays
    this.form.get('country')?.valueChanges.subscribe(country => {
      this.updatePostalCodeValidators(country);
    });

    // Application initiale des validateurs
    this.updatePostalCodeValidators(this.defaults?.country || Country.FR);
  }

  beforeCreateItem(): void {
    this.processFormData();
  }

  beforeUpdateItem(): void {
    this.processFormData();
  }

  private updatePostalCodeValidators(country: string): void {
    const postalCodeControl = this.form.get('postalCode');
    if (!postalCodeControl) return;

    // Vider les validateurs existants
    postalCodeControl.clearValidators();

    // Ajouter le validateur requis dans tous les cas
    const validators = [Validators.required];

    // Ajouter des validateurs spécifiques selon le pays
    switch (country) {
      case Country.FR:
        validators.push(Validators.pattern(/^[0-9]{5}$/));
        break;
      case 'LU': // Luxembourg
        validators.push(Validators.pattern(/^[0-9]{4}$/));
        break;
      case 'BE': // Belgique
        validators.push(Validators.pattern(/^[0-9]{4}$/));
        break;
      case 'CH': // Suisse
        validators.push(Validators.pattern(/^[0-9]{4}$/));
        break;
      case 'DE': // Allemagne
        validators.push(Validators.pattern(/^[0-9]{5}$/));
        break;
      case 'ES': // Espagne
        validators.push(Validators.pattern(/^[0-9]{5}$/));
        break;
      case 'IT': // Italie
        validators.push(Validators.pattern(/^[0-9]{5}$/));
        break;
      default:
        // Pour les autres pays, validation générique : 3 à 10 caractères alphanumériques
        validators.push(Validators.pattern(/^[A-Z0-9\s-]{3,10}$/i));
        break;
    }

    postalCodeControl.setValidators(validators);
    postalCodeControl.updateValueAndValidity();
  }

  private processFormData(): void {
    const formData = this.form.getRawValue();

    // Transformer le pays si nécessaire
    if (typeof formData.country === 'object') {
      formData.country = formData.country.value;
      this.form.patchValue({ country: formData.country });
    }

    // S'assurer que le buildingIds est présent
    if ((!formData.buildingIds || formData.buildingIds.length === 0) && this.buildingId) {
      this.form.patchValue({ buildingIds: [this.buildingId] });
    }
  }

  afterCreateItem(result?: any, error?: any): void {
    if (result) {
      this.loading = false;
      this.close(result.data);
    }
  }

  afterUpdateItem(result?: any, error?: any): void {
    if (result) {
      this.loading = false;
      this.close(result.data);
    }
  }

  // Surcharge de la méthode deleteItem pour passer le buildingId
  deleteItem(): void {
    this.loading = true;

    const subscription = this.addressService.deleteAddress(this.defaults?._id, this.buildingId).subscribe(
      result => {
        this.afterDeleteItem(result);
      },
      error => {
        this.afterDeleteItem(null, error);
        this.loading = false;
      },
    );

    this.subscription.add(subscription);
  }

  afterDeleteItem(result?: any, error?: any): void {
    if (result) {
      this.loading = false;
      this.close(true);
    }
  }

  async validatePostalCode(): Promise<void> {
    const postalCode = this.form.get('postalCode')?.value;
    const country = this.form.get('country')?.value;

    if (postalCode && country) {
      try {
        const response = await this.addressService.validatePostalCode(postalCode, country).toPromise();
        if (!response.data.isValid) {
          this.form.get('postalCode')?.setErrors({ invalidPostalCode: true });
        }
      } catch (error) {
        // Erreur lors de la validation du code postal
      }
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.createForm();

    // Messages personnalisés
    this.successCreateMessage = 'Adresse créée avec succès';
    this.successUpdateMessage = 'Adresse modifiée avec succès';
    this.successDeleteMessage = 'Adresse supprimée avec succès';
    this.errorCreateMessage = "Erreur lors de la création de l'adresse";
    this.errorUpdateMessage = "Erreur lors de la modification de l'adresse";
    this.errorDeleteMessage = "Erreur lors de la suppression de l'adresse";
  }
}
